import React, { useState, useEffect } from 'react';
import { Key, Settings, PlayArrow, InfoOutlined } from '@mui/icons-material';
import { DynamicContainer } from '../components/common/DynamicContainer';
// import { generateBreadcrumbPath } from './common/breadcrumbs'; // Commented out - breadcrumbs not needed
import { toast } from 'react-toastify';
import { Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from '@mui/material';
import { getCostXCredentialsAndCreateToken } from './TokenGeneration/CostXConnector';
import { getAutodeskCredentialsAndCreateToken, exchangeAutodeskAuthCodeForToken } from './TokenGeneration/AutodeskConnector';
import { getAutodeskDocsCredentialsAndCreateToken, exchangeAutodeskDocsAuthCodeForToken } from './TokenGeneration/AutodeskDocsConnector';
import { getQuickbookCredentialsAndCreateToken, exchangeAuthCodeForToken } from './TokenGeneration/QuickbookConnector';
import TokenParameterDrawer from './ConnectorTokenDrawer/TokenParameterDrawer'; // Import the drawer component
import { getFusionOperationCredentialsAndCreateToken } from './TokenGeneration/FusionOperationConnector';
import { getSalesforceCredentialsAndCreateToken, exchangeSalesforceAuthCodeForToken } from './TokenGeneration/SalesforceConnector';
import { getBusinessCentreCredentialsAndCreateToken, exchangeBusinessCentreAuthCodeForToken } from './TokenGeneration/BusinessCentreConnector';
import { getFusion360CredentialsAndCreateToken, exchangeFusion360AuthCodeForToken } from './TokenGeneration/Fusion360Connector';
import { getAutodeskVaultCredentialsAndCreateToken, exchangeAutodeskVaultAuthCodeForToken } from './TokenGeneration/AutodeskVaultConnector';
import { getUnanetCredentialsAndCreateToken } from './TokenGeneration/UnanetConnector';
import { getTeamsCredentialsAndCreateToken, exchangeTeamsAuthCodeForToken } from './TokenGeneration/TeamsConnector';
import { getSlackCredentialsAndCreateToken, exchangeSlackAuthCodeForToken } from './TokenGeneration/SlackConnector.jsx';
import { getGoogleDriveCredentialsAndCreateToken, exchangeGoogleDriveAuthCodeForToken } from './TokenGeneration/GoogleDriveConnector';
import { getGmailCredentialsAndCreateToken, exchangeGmailAuthCodeForToken } from './TokenGeneration/GmailConnector';
import { getCalendarCredentialsAndCreateToken, exchangeCalendarAuthCodeForToken } from './TokenGeneration/CalendarConnector';
import { getSharePointCredentialsAndCreateToken, exchangeSharePointAuthCodeForToken } from './TokenGeneration/SharePointConnector';
import { getPostgreSQLCredentialsAndCreateToken, disconnectPostgreSQLDatabase } from './TokenGeneration/PostgreSQLConnector';
import { getEgnyteCredentialsAndCreateToken } from './TokenGeneration/EgnyteConnector';
import { getSageCredentialsAndCreateToken, exchangeSageAuthCodeForToken } from './TokenGeneration/SageConnector';
import { getSageIntacctXmlCredentialsAndCreateToken } from './TokenGeneration/SageIntacctXmlConnector';
import { getSnowflakeCredentialsAndCreateToken } from './TokenGeneration/SnowflakeConnector';
import { getAsroTrackCredentialsAndCreateToken, exchangeAsroTrackAuthCodeForToken } from './TokenGeneration/AsroTrackConnector';
import { getConductorCredentialsAndCreateToken, exchangeConductorAuthCodeForToken } from './TokenGeneration/ConductorConnector';
import { getSyncTrackCredentialsAndCreateToken } from './TokenGeneration/SyncTrackConnector';
import { getLitmusCredentialsAndCreateToken } from './TokenGeneration/LitmusConnector';
import { buildConnectorParametersDecryptUrl } from '../utils/connectorParametersApi';


const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/sync-connectors`;
const MASTER_CONNECTOR_API_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/master-connector`;

// Get token from localStorage
const token = localStorage.getItem('token');
const tenantId = localStorage.getItem('tenantId');


const ConnectorToken = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [connectorTokens, setConnectorTokens] = useState([]);
  const [masterConnectors, setMasterConnectors] = useState([]);
  // New states for the parameter drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState(null);
  // New states for test dialog
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // States for fresh token display dialog
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);
  const [freshTokenDetails, setFreshTokenDetails] = useState(null);

  // States for temp_log debug dialog
  const [tempLogDialogOpen, setTempLogDialogOpen] = useState(false);
  const [tempLogLoading, setTempLogLoading] = useState(false);
  const [tempLogRows, setTempLogRows] = useState([]);
  const [tempLogError, setTempLogError] = useState(null);
  const [tempLogConnectorLabel, setTempLogConnectorLabel] = useState('Connector');

  // Check if user is CDX user
  const isCdxUser = () => {
    try {
      const cdxUser = localStorage.getItem('cdx_user');
      return cdxUser === 'true';
    } catch (error) {
      console.error('Error reading cdx_user from localStorage:', error);
      return false;
    }
  };

  // Modified useEffect to ensure sequential loading - master connectors first, then tokens
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // First get master connectors
        const masterData = await fetchMasterConnectors();
        // Then get connector tokens with master data available
        if (masterData && masterData.length > 0) {
          await fetchConnectorTokens(masterData);
        } else {
          await fetchConnectorTokens([]);
        }
      } catch (error) {
        console.error('Error loading initial data:', error);
        toast.error('Error loading data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    // Check if returning from Quickbook auth (has stored auth code)
    const completeQuickbookAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');

      if (storedAuthCode) {
        try {
          setIsLoading(true);
          const connectorId = localStorage.getItem('PendingQuickbookConnectorId');
          const connector = connectorTokens.find(c => c.id === connectorId);

          if (connector) {
            const norm = (s) =>
              (s || '')
                .toString()
                .trim()
                .toUpperCase()
                .replace(/_/g, ' ')
                .replace(/\s+/g, ' ');

            const quickbookConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              (() => {
                const typeNorm = norm(mc.mcConnectorType);
                const nameNorm = norm(mc.mcName);
                const source = typeNorm || nameNorm;
                return source.includes('QUICKBOOK');
              })()
            );

            if (quickbookConnector) {
              // Get client credentials from parameters
              const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connector.id), {
                headers: {
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                }
              });

              let clientId = 'Not available';
              let clientSecret = 'Not available';

              if (paramResponse.ok) {
                const connectorParams = await paramResponse.json();
                const encryptedClientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;
                const encryptedClientSecret = connectorParams.find(param => param.scpName === "clientsecret")?.scpValue;

                if (encryptedClientId) {
                  // For display, we'll show that it's encrypted - backend should decrypt it
                  clientId = 'Available (encrypted)';
                }
                if (encryptedClientSecret) {
                  clientSecret = 'Available (encrypted)';
                }
              }

              const tokenResponse = await exchangeAuthCodeForToken(
                storedAuthCode,
                quickbookConnector.mcClientId,
                connector.id,
                localStorage.getItem(`QuickbookRealmId_${connector.id}`)
              );

              // Display real-time token information in console immediately
              console.log('🔑 QUICKBOOKS OAUTH COMPLETION - REAL-TIME TOKEN:');
              console.log('✅ Fresh Access Token:', tokenResponse.access_token ? `${tokenResponse.access_token.substring(0, 50)}...` : 'Not provided');
              console.log('✅ New Refresh Token:', tokenResponse.refresh_token ? `${tokenResponse.refresh_token.substring(0, 30)}...` : 'Not provided');
              console.log('⏰ Token Type:', tokenResponse.token_type || 'Bearer');
              console.log('⏰ Expires In:', `${tokenResponse.expires_in || 3600} seconds (${Math.round((tokenResponse.expires_in || 3600) / 60)} minutes)`);
              console.log('🔐 Client ID:', clientId);
              console.log('🔐 Client Secret Status:', clientSecret);
              console.log('📅 Token Received At:', new Date().toISOString());
              console.log('🔗 OAuth Endpoint:', 'https://oauth.platform.intuit.com/oauth2/v1/tokens/bearer');

              // Prepare fresh token details for display
              const tokenDetails = {
                access_token: tokenResponse.access_token,
                refresh_token: tokenResponse.refresh_token,
                token_type: tokenResponse.token_type || 'Bearer',
                expires_in: tokenResponse.expires_in || 3600,
                expires_in_minutes: Math.round((tokenResponse.expires_in || 3600) / 60),
                client_id: clientId,
                client_secret: clientSecret,
                received_at: new Date().toISOString(),
                connector_name: 'QuickBooks',
                oauth_endpoint: quickbookConnector.mcRefreshUrl || 'https://oauth.platform.intuit.com/oauth2/v1/tokens/bearer'
              };

              // COMMENTED OUT: Auto-popup of token dialog - disabled for production
              // Show fresh token details BEFORE saving to database
              // setFreshTokenDetails(tokenDetails);
              // setTokenDialogOpen(true);

              const updatedData = {
                ...connector,
                token: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token,
                tokenExpiry: new Date(Date.now() + tokenResponse.expires_in * 1000).toISOString(),
                status: 'Connected'
              };

              await fetch(`${API_BASE_URL}/${connectorId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              toast.success('Quickbook connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingQuickbookConnectorId');
              localStorage.removeItem(`QuickbookRealmId_${connector.id}`);

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Quickbook authentication:', error);
          // toast.error(`Failed to complete Quickbook authentication: ${error.message}`);
          const pendingConnectorId = localStorage.getItem('PendingQuickbookConnectorId');
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingQuickbookConnectorId');
          if (pendingConnectorId) {
            localStorage.removeItem(`QuickbookRealmId_${pendingConnectorId}`);
          }
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeQuickbookAuth();
    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    // Check if returning from Salesforce auth (has stored auth code)
    const completeSalesforceAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingSalesforceId = localStorage.getItem('PendingSalesforceConnectorId');

      if (storedAuthCode && pendingSalesforceId) {
        try {
          setIsLoading(true);
          const authCodeToExchange = storedAuthCode;
          // Consume once before async work so rerenders/reloads cannot replay the same code.
          localStorage.removeItem('AuthCode');
          const connector = connectorTokens.find(c => c.id === pendingSalesforceId);

          if (connector) {
            const salesforceConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              mc.mcName === "Salesforce"
            );

            if (salesforceConnector) {
              // Fetch client ID from the API endpoint using connector instance ID
              const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connector.id),
                {
                  headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  }
                }
              );
              if (!paramResponse.ok) {
                throw new Error('Failed to fetch connector parameters');
              }
              const connectorParams = await paramResponse.json();

              // Extract client ID from response
              const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;

              if (!clientId) {
                throw new Error('Client ID not found in connector parameters');
              }

              const tokenResponse = await exchangeSalesforceAuthCodeForToken(
                authCodeToExchange,
                clientId,
                connector.id
              );

              // Duplicate callback invocation returned null; keep AuthCode for the in-flight exchange.
              if (tokenResponse == null) {
                return;
              }

              // Calculate token expiry (Salesforce tokens typically valid for 2 hours)
              const TOKEN_VALIDITY_MS = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

              // Convert issued_at from string to number if necessary
              const issuedAt = typeof tokenResponse.issued_at === 'string'
                ? parseInt(tokenResponse.issued_at)
                : tokenResponse.issued_at;

              // Calculate token expiry time based on issued_at
              const tokenExpiry = new Date(issuedAt + TOKEN_VALIDITY_MS).toISOString();

              // Get refresh URL from master connector
              const refreshUrl = salesforceConnector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token,
                tokenExpiry: tokenExpiry,
                refreshUrl: refreshUrl,
                idToken: tokenResponse.id_token,
                instanceUrl: tokenResponse.instance_url,
                signature: tokenResponse.signature,
                tokenType: tokenResponse.token_type,
                status: 'Connected'
              };

              await fetch(`${API_BASE_URL}/${pendingSalesforceId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              toast.success('Salesforce connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingSalesforceConnectorId');
              localStorage.removeItem('ConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Salesforce authentication:', error);
          // toast.error(`Failed to complete Salesforce authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingSalesforceConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeSalesforceAuth();
    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    // Check if returning from Sage (Intacct) auth (has stored auth code)
    const completeSageAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingSageId = localStorage.getItem('PendingSageConnectorId');
      const connectorType = localStorage.getItem('ConnectorType');

      if (storedAuthCode && pendingSageId && connectorType === 'Sage') {
        try {
          setIsLoading(true);
          const authCodeToExchange = storedAuthCode;
          // Consume once before async work so rerenders/reloads cannot replay the same code.
          localStorage.removeItem('AuthCode');
          const connector = connectorTokens.find(c => c.id === pendingSageId);

          if (connector) {
            const sageConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              (mc.mcConnectorType === 'SAGE_INTACCT' ||
                ((!mc.mcConnectorType || String(mc.mcConnectorType).trim() === '') &&
                  mc.mcName &&
                  (mc.mcName.toLowerCase().includes('sage') || mc.mcName.toLowerCase().includes('intacct')) &&
                  !mc.mcName.toLowerCase().includes('xml')))
            );

            if (sageConnector) {
              // Exchange auth code for token via backend
              const tokenResponse = await exchangeSageAuthCodeForToken(
                authCodeToExchange,
                connector.id
              );

              if (tokenResponse == null) {
                return;
              }

              // Calculate token expiry if provided
              const tokenExpiry = tokenResponse && tokenResponse.expires_in
                ? new Date(Date.now() + tokenResponse.expires_in * 1000).toISOString()
                : null;

              // Use a conservative refresh token expiry (90 days) if refresh token present
              const refreshTokenExpiry = tokenResponse && tokenResponse.refresh_token
                ? new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
                : null;

              const refreshUrl = sageConnector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token,
                tokenExpiry: tokenExpiry,
                refreshUrl: refreshUrl,
                refreshTokenExpiry: refreshTokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              await fetch(`${API_BASE_URL}/${pendingSageId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              toast.success('Sage connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingSageConnectorId');
              localStorage.removeItem('ConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Sage authentication:', error);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingSageConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeSageAuth();
    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    // Check if returning from DynamicsBusinessCentral auth (has stored auth code)
    const completeBusinessCentreAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingBusinessCentreId = localStorage.getItem('PendingBusinessCentreConnectorId');
      const connectorType = localStorage.getItem('ConnectorType');

      if (storedAuthCode && pendingBusinessCentreId && connectorType === 'BusinessCentre') {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingBusinessCentreId);

          if (connector) {
            const businessCentreConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              mc.mcName === "Dynamics Business Central"
            );

            if (businessCentreConnector) {
              // Fetch client ID from the API endpoint
              const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connector.id),
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                }
              );
              if (!paramResponse.ok) {
                throw new Error('Failed to fetch connector parameters');
              }
              const connectorParams = await paramResponse.json();

              // Extract client ID from response
              const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;

              if (!clientId) {
                throw new Error('Client ID not found in connector parameters');
              }

              const tokenResponse = await exchangeBusinessCentreAuthCodeForToken(
                storedAuthCode,
                clientId,
                connector.mcId
              );

              // Calculate token expiry
              const tokenExpiry = new Date(Date.now() + tokenResponse.expires_in * 1000).toISOString();

              // Calculate the refresh token expiry (long-lived, typically 90 days for Microsoft)
              const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
              const refreshTokenExpiry = new Date(Date.now() + NINETY_DAYS_MS).toISOString();

              // Get refresh URL from master connector or use default Microsoft refresh URL
              const refreshUrl = businessCentreConnector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token,
                tokenExpiry: tokenExpiry,
                refreshUrl: refreshUrl,
                refreshTokenExpiry: refreshTokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              await fetch(`${API_BASE_URL}/${pendingBusinessCentreId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              toast.success('DynamicsBusinessCentral connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingBusinessCentreConnectorId');
              localStorage.removeItem('ConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing DynamicsBusinessCentral authentication:', error);
          // toast.error(`Failed to complete DynamicsBusinessCentral authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingBusinessCentreConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeBusinessCentreAuth();
    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    // Check if returning from Fusion360 auth (has stored auth code)
    const completeFusion360Auth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingFusion360Id = localStorage.getItem('PendingFusion360ConnectorId');
      const connectorType = localStorage.getItem('ConnectorType');

      if (storedAuthCode && pendingFusion360Id && connectorType === 'Fusion360') {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingFusion360Id);

          if (connector) {
            const fusion360Connector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              mc.mcName === "Fusion 360"
            );

            if (fusion360Connector) {
              // Fetch client ID and client secret from the API endpoint
              const paramResponse = await fetch(buildConnectorParametersDecryptUrl(connector.id), {
                headers: {
                  Accept: 'application/json',
                  Authorization: `Bearer ${token}`,
                },
              });
              if (!paramResponse.ok) {
                throw new Error('Failed to fetch connector parameters');
              }
              const connectorParams = await paramResponse.json();

              // Extract client ID and client secret from response
              const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;

              if (!clientId) {
                throw new Error('Client ID not found in connector parameters');
              }

              const tokenResponse = await exchangeFusion360AuthCodeForToken(
                storedAuthCode,
                clientId,
                connector.mcId
              );

              // Calculate token expiry dates
              const primaryExpiresIn = parseInt(tokenResponse.primary_token?.expires_in) || 3600;
              const secondaryExpiresIn = parseInt(tokenResponse.secondary_token?.expires_in) || 3600;

              // Calculate the 14-day expiry date for refresh token
              const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
              const refreshTokenExpiry = new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString();

              // Get refresh URL from master connector
              const refreshUrl = fusion360Connector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse.primary_token?.access_token,
                refreshToken: tokenResponse.primary_token?.refresh_token,
                tokenExpiry: new Date(Date.now() + (primaryExpiresIn * 1000)).toISOString(),
                secondaryToken: tokenResponse.secondary_token?.access_token,
                secondaryTokenExpiry: new Date(Date.now() + (secondaryExpiresIn * 1000)).toISOString(),
                refreshUrl: refreshUrl,
                refreshTokenExpiry: refreshTokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              await fetch(`${API_BASE_URL}/${pendingFusion360Id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              toast.success('Fusion360 connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingFusion360ConnectorId');
              localStorage.removeItem('ConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Fusion360 authentication:', error);
          // toast.error(`Failed to complete Fusion360 authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingFusion360ConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeFusion360Auth();
    }
  }, [masterConnectors, connectorTokens]);

  // Add this within the same useEffect or create a new one after the Quickbook auth effect
  useEffect(() => {
    // Check if returning from Autodesk auth (has stored auth code)
    const completeAutodeskAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingAutodeskId = localStorage.getItem('PendingAutodeskConnectorId');

      if (storedAuthCode && pendingAutodeskId) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingAutodeskId);

          if (connector) {
            const norm = (s) =>
              (s || '')
                .toString()
                .trim()
                .toUpperCase()
                .replace(/_/g, ' ')
                .replace(/\s+/g, ' ');

            const autodeskConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              (() => {
                const nameNorm = norm(mc.mcName);
                const typeNorm = norm(mc.mcConnectorType);
                const source = typeNorm || nameNorm;
                return source.includes('AUTODESK') &&
                  !source.includes('DOCS') &&
                  !source.includes('VAULT');
              })()
            );

            if (autodeskConnector) {
              const tokenResponse = await exchangeAutodeskAuthCodeForToken(storedAuthCode, connector.id);

              // Duplicate Strict Mode / double effect: exchange skipped; keep AuthCode for in-flight call
              if (tokenResponse == null) {
                return;
              }

              // Tokens already persisted inside exchangeAutodeskAuthCodeForToken (PUT sync-connectors)
              toast.success('Autodesk connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingAutodeskConnectorId');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Autodesk authentication:', error);
          // toast.error(`Failed to complete Autodesk authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingAutodeskConnectorId');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeAutodeskAuth();
    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    // Check if returning from Autodesk Docs auth (has stored auth code)
    const completeAutodeskDocsAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingAutodeskDocsId = localStorage.getItem('PendingAutodeskDocsConnectorId');

      if (storedAuthCode && pendingAutodeskDocsId) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingAutodeskDocsId);

          if (connector) {
            const autodeskDocsConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              mc.mcName === "Autodesk Docs"
            );

            if (autodeskDocsConnector) {
              const tokenResponse = await exchangeAutodeskDocsAuthCodeForToken(
                storedAuthCode,
                connector.mcId
              );

              // Calculate token expiry dates
              const primaryExpiresIn = parseInt(tokenResponse.primary_token?.expires_in) || 3600;
              const secondaryExpiresIn = parseInt(tokenResponse.secondary_token?.expires_in) || 3600;

              // Calculate the 14-day expiry date for refresh token
              const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
              const refreshTokenExpiry = new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString();

              // Get refresh URL from master connector
              const refreshUrl = autodeskDocsConnector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse.primary_token?.access_token,
                refreshToken: tokenResponse.primary_token?.refresh_token,
                tokenExpiry: new Date(Date.now() + (primaryExpiresIn * 1000)).toISOString(),
                secondaryToken: tokenResponse.secondary_token?.access_token,
                secondaryTokenExpiry: new Date(Date.now() + (secondaryExpiresIn * 1000)).toISOString(),
                refreshUrl: refreshUrl,
                refreshTokenExpiry: refreshTokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              await fetch(`${API_BASE_URL}/${pendingAutodeskDocsId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              toast.success('Autodesk Docs connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingAutodeskDocsConnectorId');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Autodesk Docs authentication:', error);
          // toast.error(`Failed to complete Autodesk Docs authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingAutodeskDocsConnectorId');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeAutodeskDocsAuth();
    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    const spRunRef = { current: false };

    const completeSharePointConnection = async () => {
      // Require explicit pending keys (prevents "connect every render")
      const pendingSharePointId = localStorage.getItem('PendingSharePointConnectorId');
      const pendingMcId = localStorage.getItem('PendingSharePointMcId');

      if (!pendingSharePointId || !pendingMcId || masterConnectors.length === 0 || connectorTokens.length === 0) {
        return;
      }

      const connector = connectorTokens.find(c =>
        c.id === pendingSharePointId &&
        (c.provider === 'SharePoint' || c.mcName === 'SharePoint' || c.type === 'SharePoint')
      );

      if (!connector) return;

      // If already connected and not expiring soon, do nothing
      const isConnected = connector.status === 'Connected';
      const notExpiringSoon =
        connector.tokenExpiry &&
        new Date(connector.tokenExpiry).getTime() > Date.now() + 5 * 60 * 1000;
      if (isConnected && notExpiringSoon) {
        // cleanup stale pending markers
        localStorage.removeItem('PendingSharePointConnectorId');
        localStorage.removeItem('PendingSharePointMcId');
        return;
      }

      // Re-entrancy guard
      if (spRunRef.current) return;
      spRunRef.current = true;

      try {
        setIsLoading(true);

        // SharePoint is app-only — no auth code needed
        localStorage.removeItem('AuthCode');

        // Find SharePoint master connector
        const sharePointConnector = masterConnectors.find(mc =>
          (mc.mcRecId?.toString() === connector.mcId?.toString() ||
            mc.mcId?.toString() === connector.mcId?.toString()) &&
          mc.mcName === 'SharePoint'
        );

        if (!sharePointConnector) {
          throw new Error('SharePoint master connector not found');
        }

        const mcId = pendingMcId || connector.mcId || connector.mcRecId;
        if (!mcId) {
          throw new Error('Missing SharePoint Master Connector ID (mcId)');
        }

        // Call backend to get app-only access token
        const tokenUrl = `${process.env.REACT_APP_BACKEND_URL}synctrack/sharepoint/validToken?mcId=${encodeURIComponent(mcId)}`;
        const tokenResponse = await fetch(tokenUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        });

        if (!tokenResponse.ok) {
          const errText = await tokenResponse.text();
          throw new Error(`Failed to fetch SharePoint token: ${tokenResponse.status} ${errText}`);
        }

        const tokenData = await tokenResponse.json();

        if (!tokenData?.access_token) {
          throw new Error('Backend did not return access_token');
        }

        let updatedData;

        // Handle SharePoint specific token response
        if (sharePointConnector?.mcName === 'SharePoint' && tokenData) {
          const expiresInMs = (tokenData.expires_in || 3600) * 1000;
          updatedData = {
            ...connector,
            token: tokenData.access_token,
            refreshToken: null, // App-only has no refresh token
            tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
            refreshUrl: null, // App-only doesn't need refresh URL
            refreshTokenExpiry: null, // App-only has no refresh token expiry
            status: 'Connected',
            connectedTime: new Date().toISOString(),
            scope: 'app-only'
          };
        } else {
          // Original token handling for other connector types
          const expiresIn = Number(tokenData?.expires_in ?? 3600);
          updatedData = {
            ...connector,
            token: tokenData.access_token,
            refreshToken: null,
            tokenExpiry: new Date(Date.now() + expiresIn * 1000).toISOString(),
            status: 'Connected',
            connectedTime: new Date().toISOString(),
            scope: 'app-only'
          };
        }


        // Update the connector
        const putRes = await fetch(`${API_BASE_URL}/${pendingSharePointId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedData),
        });

        if (!putRes.ok) {
          const errText = await putRes.text();
          throw new Error(`Failed to update SharePoint connector: ${putRes.status} ${errText}`);
        }

        toast.success('SharePoint connector connected successfully');

        // Clear pending flags so the effect won't re-run this path
        localStorage.removeItem('PendingSharePointConnectorId');
        localStorage.removeItem('PendingSharePointMcId');

        await refreshData();
      } catch (error) {
        console.error('Error connecting SharePoint (app-only):', error);
        toast.error(`Failed to connect SharePoint: ${error.message}`);
        // Clear flags to avoid retry loop
        localStorage.removeItem('PendingSharePointConnectorId');
        localStorage.removeItem('PendingSharePointMcId');
      } finally {
        setIsLoading(false);
        spRunRef.current = false;
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeSharePointConnection();

    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    // Check if returning from Teams auth (has stored auth code)
    const completeTeamsAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingTeamsId = localStorage.getItem('PendingTeamsConnectorId');
      const connectorType = localStorage.getItem('ConnectorType');

      if (storedAuthCode && pendingTeamsId && connectorType === 'Teams') {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingTeamsId);

          if (connector) {
            const teamsConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              mc.mcName === "Microsoft Teams"
            );

            if (teamsConnector) {
              // Fetch client ID from the API endpoint
              const paramResponse = await fetch(
                buildConnectorParametersDecryptUrl(connector.id),
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                }
              );
              if (!paramResponse.ok) {
                throw new Error('Failed to fetch connector parameters');
              }
              const connectorParams = await paramResponse.json();

              // Extract client ID from response
              const clientId = connectorParams.find(param => param.scpName === "clientid")?.scpValue;

              if (!clientId) {
                throw new Error('Client ID not found in connector parameters');
              }

              const tokenResponse = await exchangeTeamsAuthCodeForToken(
                storedAuthCode,
                clientId,
                connector.mcId
              );

              // Calculate token expiry
              const tokenExpiry = new Date(Date.now() + tokenResponse.expires_in * 1000).toISOString();

              // Calculate the refresh token expiry (long-lived, typically 90 days for Microsoft)
              const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;
              const refreshTokenExpiry = new Date(Date.now() + NINETY_DAYS_MS).toISOString();

              // Get refresh URL from master connector or use default Microsoft refresh URL
              const refreshUrl = teamsConnector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token,
                tokenExpiry: tokenExpiry,
                refreshUrl: refreshUrl,
                refreshTokenExpiry: refreshTokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              await fetch(`${API_BASE_URL}/${pendingTeamsId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              toast.success('Microsoft Teams connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingTeamsConnectorId');
              localStorage.removeItem('ConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Microsoft Teams authentication:', error);
          // toast.error(`Failed to complete Microsoft Teams authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingTeamsConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeTeamsAuth();
    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    const completeSlackAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingSlackId = localStorage.getItem('PendingSlackConnectorId');
      const connectorType = localStorage.getItem('ConnectorType');

      if (storedAuthCode && pendingSlackId && (connectorType === 'Slack' || connectorType === 'slack')) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingSlackId);

          if (connector) {
            const slackMasterConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() || mc.mcId?.toString() === connector.mcId?.toString()) &&
              (mc.mcName === 'Slack' || (mc.mcName && mc.mcName.toLowerCase().includes('slack')))
            );

            if (slackMasterConnector) {
              const tokenResponse = await exchangeSlackAuthCodeForToken(
                storedAuthCode,
                pendingSlackId,
                connector.mcId
              );

              const tokenExpiry = tokenResponse.expires_in > 0
                ? new Date(Date.now() + tokenResponse.expires_in * 1000).toISOString()
                : new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000).toISOString();

              const updatedData = {
                ...connector,
                token: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token || null,
                tokenExpiry: tokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString(),
              };

              await fetch(`${API_BASE_URL}/${pendingSlackId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData),
              });

              toast.success('Slack connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingSlackConnectorId');
              localStorage.removeItem('ConnectorType');
              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Slack authentication:', error);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingSlackConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeSlackAuth();
    }
  }, [masterConnectors, connectorTokens]);

  useEffect(() => {
    // Check if returning from Google Drive auth (has stored auth code)
    const completeGoogleDriveAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingGoogleDriveId = localStorage.getItem('PendingGoogleDriveConnectorId');

      if (storedAuthCode && pendingGoogleDriveId) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingGoogleDriveId);

          if (connector) {
            const googleConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              mc.mcName === "GoogleDrive"
            );

            if (googleConnector) {
              // Fetch client ID/secret (your backend may not need them, but we mirror your Autodesk pattern)
              const paramResponse = await fetch(
                buildConnectorParametersDecryptUrl(connector.id),
                {
                  headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  }
                }
              );
              if (!paramResponse.ok) throw new Error('Failed to fetch connector parameters');
              const connectorParams = await paramResponse.json();
              const clientId = connectorParams.find(p => p.scpName === "clientid")?.scpValue;
              if (!clientId) throw new Error('Client ID not found in connector parameters');

              const redirectUri = process.env.REACT_APP_OAUTH_REDIRECT_URI;

              // Exchange auth code for tokens
              const tokenResponse = await exchangeGoogleDriveAuthCodeForToken(
                storedAuthCode,
                clientId,
                connector.mcId,
                redirectUri
              );

              // Compute expiries and refresh URL
              const primaryExpiresIn = parseInt(tokenResponse?.expires_in, 10) || 3600;
              const ONE_EIGHTY_DAYS_MS = 180 * 24 * 60 * 60 * 1000;
              const refreshTokenExpiry = new Date(Date.now() + ONE_EIGHTY_DAYS_MS).toISOString();
              const refreshUrl = googleConnector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse?.access_token,
                refreshToken: tokenResponse?.refresh_token || connector.refreshToken || null,
                tokenExpiry: new Date(Date.now() + primaryExpiresIn * 1000).toISOString(),
                refreshUrl,
                refreshTokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              await fetch(`${API_BASE_URL}/${pendingGoogleDriveId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              toast.success('GoogleDrive connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingGoogleDriveConnectorId');
              localStorage.removeItem('ConnectorType'); // safe to clear if you use it elsewhere

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing GoogleDrive authentication:', error);
          // toast.error(`Failed to complete GoogleDrive authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingGoogleDriveConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeGoogleDriveAuth();
    }
  }, [masterConnectors, connectorTokens]);

  // Gmail OAuth completion handler
  useEffect(() => {
    const completeGmailAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingGmailId = localStorage.getItem('PendingGmailConnectorId');

      if (storedAuthCode && pendingGmailId) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingGmailId);

          if (connector) {
            const gmailConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              mc.mcName === "Gmail"
            );

            if (gmailConnector) {
              // Exchange auth code for tokens using backend
              const tokenResponse = await exchangeGmailAuthCodeForToken(
                storedAuthCode,
                pendingGmailId,
                connector.mcId
              );

              // Compute expiries and refresh URL
              const primaryExpiresIn = parseInt(tokenResponse?.expires_in, 10) || 3600;
              const ONE_EIGHTY_DAYS_MS = 180 * 24 * 60 * 60 * 1000;
              const refreshTokenExpiry = new Date(Date.now() + ONE_EIGHTY_DAYS_MS).toISOString();
              const refreshUrl = gmailConnector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse?.access_token,
                refreshToken: tokenResponse?.refresh_token || connector.refreshToken || null,
                tokenExpiry: new Date(Date.now() + primaryExpiresIn * 1000).toISOString(),
                refreshUrl,
                refreshTokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              console.log('📝 Gmail: Saving tokens to backend...', {
                url: `${API_BASE_URL}/${pendingGmailId}`,
                hasAccessToken: !!tokenResponse?.access_token,
                hasRefreshToken: !!tokenResponse?.refresh_token,
                tokenExpiry: updatedData.tokenExpiry
              });

              const updateResponse = await fetch(`${API_BASE_URL}/${pendingGmailId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              if (!updateResponse.ok) {
                const errorText = await updateResponse.text();
                console.error('❌ Gmail: Failed to save tokens to backend:', errorText);
                throw new Error(`Failed to save tokens: ${updateResponse.status} ${errorText}`);
              }

              const savedConnector = await updateResponse.json();
              console.log('✅ Gmail: Tokens saved successfully', {
                id: savedConnector.id,
                hasToken: !!savedConnector.token,
                hasRefreshToken: !!savedConnector.refreshToken
              });

              toast.success('Gmail connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingGmailConnectorId');
              localStorage.removeItem('PendingGmailMcId');
              localStorage.removeItem('OAuthConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Gmail authentication:', error);
          toast.error(`Failed to complete Gmail authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingGmailConnectorId');
          localStorage.removeItem('PendingGmailMcId');
          localStorage.removeItem('OAuthConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeGmailAuth();
    }
  }, [masterConnectors, connectorTokens]);

  // Calendar OAuth completion handler
  useEffect(() => {
    const completeCalendarAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingCalendarId = localStorage.getItem('PendingCalendarConnectorId');

      if (storedAuthCode && pendingCalendarId) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingCalendarId);

          if (connector) {
            const calendarConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              mc.mcName === "Calendar"
            );

            if (calendarConnector) {
              // Exchange auth code for tokens using backend
              const tokenResponse = await exchangeCalendarAuthCodeForToken(
                storedAuthCode,
                pendingCalendarId,
                connector.mcId
              );

              // Compute expiries and refresh URL
              const primaryExpiresIn = parseInt(tokenResponse?.expires_in, 10) || 3600;
              const ONE_EIGHTY_DAYS_MS = 180 * 24 * 60 * 60 * 1000;
              const refreshTokenExpiry = new Date(Date.now() + ONE_EIGHTY_DAYS_MS).toISOString();
              const refreshUrl = calendarConnector.mcRefreshUrl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse?.access_token,
                refreshToken: tokenResponse?.refresh_token || connector.refreshToken || null,
                tokenExpiry: new Date(Date.now() + primaryExpiresIn * 1000).toISOString(),
                refreshUrl,
                refreshTokenExpiry,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              console.log('📝 Calendar: Saving tokens to backend...', {
                url: `${API_BASE_URL}/${pendingCalendarId}`,
                hasAccessToken: !!tokenResponse?.access_token,
                hasRefreshToken: !!tokenResponse?.refresh_token,
                tokenExpiry: updatedData.tokenExpiry
              });

              const updateResponse = await fetch(`${API_BASE_URL}/${pendingCalendarId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              if (!updateResponse.ok) {
                const errorText = await updateResponse.text();
                console.error('❌ Calendar: Failed to save tokens to backend:', errorText);
                throw new Error(`Failed to save tokens: ${updateResponse.status} ${errorText}`);
              }

              const savedConnector = await updateResponse.json();
              console.log('✅ Calendar: Tokens saved successfully', {
                id: savedConnector.id,
                hasToken: !!savedConnector.token,
                hasRefreshToken: !!savedConnector.refreshToken
              });

              toast.success('Calendar connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingCalendarConnectorId');
              localStorage.removeItem('PendingCalendarMcId');
              localStorage.removeItem('OAuthConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Calendar authentication:', error);
          toast.error(`Failed to complete Calendar authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingCalendarConnectorId');
          localStorage.removeItem('PendingCalendarMcId');
          localStorage.removeItem('OAuthConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeCalendarAuth();
    }
  }, [masterConnectors, connectorTokens]);

  // AsroTrack OAuth completion handler
  useEffect(() => {
    const completeAsroTrackAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingAsroTrackId = localStorage.getItem('PendingAsroTrackConnectorId');

      if (storedAuthCode && pendingAsroTrackId) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingAsroTrackId);

          if (connector) {
            const asroTrackMaster = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              (mc.mcConnectorType === 'ASROTRACK' ||
                (mc.mcName && mc.mcName.toLowerCase().replace(/\s+/g, '') === 'asrotrack'))
            );

            if (asroTrackMaster) {
              const tokenResponse = await exchangeAsroTrackAuthCodeForToken(
                storedAuthCode,
                pendingAsroTrackId
              );

              if (tokenResponse == null) {
                return;
              }

              const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
              const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
              const refreshUrl = asroTrackMaster.mcRefreshUrl || asroTrackMaster.mcTokenurl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token || connector.refreshToken || null,
                tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
                refreshUrl,
                refreshTokenExpiry: tokenResponse.refresh_token
                  ? new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString()
                  : null,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              const updateResponse = await fetch(`${API_BASE_URL}/${pendingAsroTrackId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              if (!updateResponse.ok) {
                const errorText = await updateResponse.text();
                throw new Error(`Failed to save tokens: ${updateResponse.status} ${errorText}`);
              }

              toast.success('AsroTrack connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingAsroTrackConnectorId');
              localStorage.removeItem('ConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing AsroTrack authentication:', error);
          toast.error(`Failed to complete AsroTrack authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingAsroTrackConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeAsroTrackAuth();
    }
  }, [masterConnectors, connectorTokens]);

  // Conductor OAuth completion handler
  useEffect(() => {
    const completeConductorAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingConductorId = localStorage.getItem('PendingConductorConnectorId');

      if (storedAuthCode && pendingConductorId) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingConductorId);

          if (connector) {
            const conductorMaster = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              (mc.mcConnectorType === 'CONDUCTOR' ||
                (mc.mcName && mc.mcName.toLowerCase().replace(/\s+/g, '') === 'conductor'))
            );

            if (conductorMaster) {
              const tokenResponse = await exchangeConductorAuthCodeForToken(
                storedAuthCode,
                pendingConductorId
              );

              if (tokenResponse == null) {
                return;
              }

              const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
              const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
              const refreshUrl = conductorMaster.mcRefreshUrl || conductorMaster.mcTokenurl || connector.refreshUrl;

              const updatedData = {
                ...connector,
                token: tokenResponse.access_token,
                refreshToken: tokenResponse.refresh_token || connector.refreshToken || null,
                tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
                refreshUrl,
                refreshTokenExpiry: tokenResponse.refresh_token
                  ? new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString()
                  : null,
                status: 'Connected',
                connectedTime: new Date().toISOString()
              };

              const updateResponse = await fetch(`${API_BASE_URL}/${pendingConductorId}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updatedData)
              });

              if (!updateResponse.ok) {
                const errorText = await updateResponse.text();
                throw new Error(`Failed to save tokens: ${updateResponse.status} ${errorText}`);
              }

              toast.success('Conductor connector connected successfully');
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingConductorConnectorId');
              localStorage.removeItem('ConnectorType');

              await refreshData();
            }
          }
        } catch (error) {
          console.error('Error completing Conductor authentication:', error);
          toast.error(`Failed to complete Conductor authentication: ${error.message}`);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingConductorConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeConductorAuth();
    }
  }, [masterConnectors, connectorTokens]);

  // Egnyte OAuth completion handler
  // Note: Egnyte uses password grant OAuth, so no redirect handling is needed
  // The getEgnyteCredentialsAndCreateToken function handles everything directly
  // This useEffect is kept for backward compatibility but won't be triggered for password grant
  useEffect(() => {
    const completeEgnyteAuth = async () => {
      const storedAuthCode = localStorage.getItem('AuthCode');
      const pendingEgnyteId = localStorage.getItem('PendingEgnyteConnectorId');

      // Password grant doesn't use AuthCode, so this won't execute for new Egnyte connections
      // Keeping for backward compatibility with old OAuth flow if needed
      if (storedAuthCode && pendingEgnyteId) {
        try {
          setIsLoading(true);
          const connector = connectorTokens.find(c => c.id === pendingEgnyteId);

          if (connector) {
            const egnyteConnector = masterConnectors.find(mc =>
              (mc.mcRecId?.toString() === connector.mcId?.toString() ||
                mc.mcId?.toString() === connector.mcId?.toString()) &&
              (mc.mcName === "Egnyte" || mc.mcName === "Egnyte Storage")
            );

            if (egnyteConnector) {
              // This code path is for old OAuth authorization_code flow
              // Password grant flow is handled directly in getEgnyteCredentialsAndCreateToken
              console.warn('Egnyte OAuth code flow detected - this should not happen with password grant');

              // Clean up localStorage
              localStorage.removeItem('AuthCode');
              localStorage.removeItem('PendingEgnyteConnectorId');
              localStorage.removeItem('ConnectorType');
            }
          }
        } catch (error) {
          console.error('Error in Egnyte auth completion:', error);
          localStorage.removeItem('AuthCode');
          localStorage.removeItem('PendingEgnyteConnectorId');
          localStorage.removeItem('ConnectorType');
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (masterConnectors.length > 0 && connectorTokens.length > 0) {
      completeEgnyteAuth();
    }
  }, [masterConnectors, connectorTokens]);


  const fetchMasterConnectors = async () => {
    try {
      console.log('Fetching master connectors...');
      const authToken = localStorage.getItem('token');
      const response = await fetch(MASTER_CONNECTOR_API_URL, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Master Connectors Response:', data);
      setMasterConnectors(data);
      return data;
    } catch (error) {
      console.error('Error fetching master connectors:', error);
      toast.error(`Error fetching master connectors: ${error.message}`);
      return [];
    }
  };

  // Modified to accept masterConnectorsData as a parameter
  const fetchConnectorTokens = async (masterConnectorsData = null, page = 0, size = 1000) => {
    try {
      console.log('Fetching connector tokens...');
      const response = await fetch(`${API_BASE_URL}/tenant/${tenantId}?page=${page}&size=${size}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Response:', data);

      // Use the passed master connectors data if available, otherwise use state
      const mcData = masterConnectorsData || masterConnectors;

      // Handle paginated response: { content, total, page, pageSize }
      const isPaginated = data && typeof data === 'object' && Array.isArray(data.content);
      const list = isPaginated ? data.content : (Array.isArray(data) ? data : []);
      const total = isPaginated ? Number(data.total) || 0 : list.length;

      // Map the response data to match your column definitions and add an id field
      const mappedData = list.map(item => {
        // Find the corresponding master connector to get its name
        const masterConnector = mcData.find(mc =>
          mc.mcRecId?.toString() === item.mcId?.toString() ||
          mc.mcId?.toString() === item.mcId?.toString()
        );

        const mcName = masterConnector ? masterConnector.mcName : `Unknown (ID: ${item.mcId})`;

        return {
          ...item,
          // Use UUID id as the primary key for DataGrid
          id: item.id, // Use the UUID as primary identifier
          // Store both ID and name
          mcId: item.mcId, // Keep the original mcId for API calls
          mcName: mcName, // Add the name for display
          mcConnectorType: masterConnector?.mcConnectorType || null, // Add connector type for matching
          // Ensure all fields are properly mapped
          refreshUrl: item.refreshUrl || item.refreshUrl,
          // Keep status exactly as it comes from the API
          status: item.status
        };
      });

      console.log('Mapped data for grid:', mappedData);
      setConnectorTokens(mappedData);

      return mappedData;
    } catch (error) {
      console.error('Error details:', error);
      toast.error(`Error fetching connector tokens: ${error.message}`);
      return [];
    }
  };

  // Refresh function that ensures master connectors are loaded before tokens
  const refreshData = async () => {
    setIsLoading(true);
    try {
      const masterData = await fetchMasterConnectors();
      await fetchConnectorTokens(masterData);
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Error refreshing data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (data) => {
    try {
      // Remove empty fields or null values that might cause validation issues
      const cleanData = Object.fromEntries(
        Object.entries(data).filter(([_, v]) => v !== null && v !== '')
      );

      // Always set initial status to Disconnected
      cleanData.status = 'Disconnected';

      // Handle the refreshUrl/refreshUrl field name based on backend
      if (cleanData.refreshUrl) {
        cleanData.refreshUrl = cleanData.refreshUrl; // Match backend field name
        delete cleanData.refreshUrl;
      }

      console.log('Creating connector token with clean data:', cleanData);

      const response = await fetch(API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(cleanData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Create response:', responseData);
      toast.success('Connector token created successfully');
      await refreshData();
      return responseData;
    } catch (error) {
      console.error('Create error full details:', error);
      toast.error(`Error creating connector token: ${error.message}`);
      throw error;
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      // Get current status from the grid data to preserve it
      const currentItem = connectorTokens.find(item => item.id === id);

      if (!currentItem) {
        throw new Error(`Connector with ID ${id} not found`);
      }

      // Use the existing status directly
      const currentStatus = currentItem ? currentItem.status : 'Disconnected';

      // Prepare the data for update
      const updatedData = {
        ...data,
        status: currentStatus // Keep the existing status
      };

      // Handle the refreshUrl/refreshUrl field name based on backend
      if (updatedData.refreshUrl) {
        updatedData.refreshUrl = updatedData.refreshUrl; // Match backend field name
        delete updatedData.refreshUrl;
      }

      console.log('Updating connector token:', id, updatedData);

      // Using the UUID for API path
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('Update response:', responseData);
      toast.success('Connector token updated successfully');
      await refreshData();
      return responseData;
    } catch (error) {
      console.error('Update error full details:', error);
      toast.error(`Error updating connector token: ${error.message}`);
      throw error;
    }
  };

  const parseApiErrorMessage = async (response, fallback) => {
    const errorText = await response.text();
    console.error('Error response text:', errorText);
    try {
      const errorData = JSON.parse(errorText);
      if (errorData?.message) {
        return errorData.message;
      }
    } catch (e) {
      // body is not JSON
    }
    return errorText || fallback || `HTTP error! status: ${response.status}`;
  };

  const getDeleteBlockReason = async (row) => {
    const id = typeof row === 'object' ? row?.id : row;
    if (!id) {
      return null;
    }
    const response = await fetch(`${API_BASE_URL}/${id}/assigned-flows`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`,
      }
    });
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data?.assigned ? (data.message || null) : null;
  };

  const handleDelete = async (id) => {
    try {
      console.log('Deleting connector token with UUID:', id);

      // Using the UUID for API path
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (!response.ok) {
        throw new Error(await parseApiErrorMessage(response, `HTTP error! status: ${response.status}`));
      }

      toast.success('Connector token deleted successfully');
      await refreshData();
      return true;
    } catch (error) {
      console.error('Delete error full details:', error);
      throw error;
    }
  };

  // Add a new state to track connections in progress
  // Add a new state to track connections in progress
  const [connectingIds, setConnectingIds] = useState([]);

  // Updated handleConnectionToggle with correct token expiry time
  const handleConnectionToggle = async (id, currentStatus) => {
    try {
      // Prevent duplicate clicks
      if (connectingIds.includes(id)) {
        return;
      }
      // Add this id to the connecting ids
      setConnectingIds(prev => [...prev, id]);
      // Get current item data
      const currentItem = connectorTokens.find(item => item.id === id);
      if (!currentItem) {
        throw new Error(`Connector with ID ${id} not found`);
      }
      // Create a copy of the current item as the base for our update
      let updatedData = { ...currentItem };
      // Determine the new status based on the current status
      // We'll only apply this at the end after API success
      const newStatus = currentStatus === 'Connected' ? 'Disconnected' : 'Connected';

      // Handle disconnection for PostgreSQL
      if (newStatus === 'Disconnected') {
        const masterConnector = masterConnectors.find(mc =>
        (mc.mcRecId?.toString() === currentItem.mcId?.toString() ||
          mc.mcId?.toString() === currentItem.mcId?.toString())
        );

        const disconnectMcType = (masterConnector?.mcConnectorType || masterConnector?.mcName || '').toString().toUpperCase();
        if (masterConnector && (disconnectMcType === 'POSTGRESQL DATABASE' || disconnectMcType === 'POSTGRESQL')) {
          try {
            await disconnectPostgreSQLDatabase(id);
            updatedData = {
              ...updatedData,
              token: null,
              status: 'Disconnected',
              connectedTime: null
            };
            // Update status and make API call
            updatedData.status = newStatus;
            const response = await fetch(`${API_BASE_URL}/${id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify(updatedData)
            });
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            toast.success('PostgreSQL database disconnected successfully');
            await refreshData();
            setConnectingIds(prev => prev.filter(item => item !== id));
            return;
          } catch (error) {
            console.error('PostgreSQL disconnect error:', error);
            toast.error(`Failed to disconnect PostgreSQL: ${error.message}`);
            setConnectingIds(prev => prev.filter(item => item !== id));
            return null;
          }
        }
      }

      // Only process connection logic if connecting
      if (newStatus === 'Connected') {
        try {
          // Find corresponding master connector
          const masterConnector = masterConnectors.find(mc =>
          (mc.mcRecId?.toString() === currentItem.mcId?.toString() ||
            mc.mcId?.toString() === currentItem.mcId?.toString())
          );
          if (!masterConnector) {
            throw new Error('Master connector not found');
          }
          console.log('Found master connector:', masterConnector);
          // Use a switch statement to call the appropriate connector function
          // Use mcConnectorType for matching (supports variants like "Quickbook Production Url")
          // Fallback to mcName for backward compatibility
          // Normalize for switch: trim, uppercase, underscores → spaces (AUTODESK_DOCS → AUTODESK DOCS)
          const norm = (s) =>
            (s || '')
              .toString()
              .trim()
              .toUpperCase()
              .replace(/_/g, ' ')
              .replace(/\s+/g, ' ');
          const nameNorm = norm(masterConnector.mcName);
          const typeNorm = norm(masterConnector.mcConnectorType);
          let connectorType = typeNorm || nameNorm;
          if (nameNorm === 'AUTODESK DOCS') {
            connectorType = 'AUTODESK DOCS';
          }
          // SyncTrack master may still be typed as generic OAuth2 — match by name.
          if (nameNorm === 'SYNCTRACK') {
            connectorType = 'SYNCTRACK';
          }
          const mcType = connectorType;
          let tokenResponse;
          switch (connectorType) {
            case 'COSTX':
              tokenResponse = await getCostXCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'AUTODESK':
              tokenResponse = await getAutodeskCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Add early exit for Autodesk if redirecting
              if (!tokenResponse) {
                // If null is returned, it means we're redirecting to auth page
                // Exit early without changing status
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'FUSION_OPERATION':
            case 'FUSION OPERATION':
            case 'FUSIONOPERATION':
              tokenResponse = await getFusionOperationCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'SALESFORCE':
              tokenResponse = await getSalesforceCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Early exit for Salesforce if redirecting
              if (!tokenResponse) {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'SAGE INTACCT XML':
            case 'SAGE_INTACCT_XML':
              tokenResponse = await getSageIntacctXmlCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'SAGE':
            case 'SAGE INTACCT':
            case 'SAGE_INTACCT':
              tokenResponse = await getSageCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Early exit for Sage if redirecting to auth page
              if (!tokenResponse) {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'SNOWFLAKE':
              tokenResponse = await getSnowflakeCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'DYNAMICS BUSINESS CENTRAL':
              tokenResponse = await getBusinessCentreCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'FUSION 360':
              tokenResponse = await getFusion360CredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'VAULT':
            case 'AUTODESK VAULT':
            case 'AUTODESK_VAULT':
            case 'AUTODESKVAULT':
              tokenResponse = await getAutodeskVaultCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'AUTODESK_DOCS':
            case 'AUTODESK DOCS':
              tokenResponse = await getAutodeskDocsCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Early exit for Autodesk Docs if redirecting
              if (!tokenResponse) {
                // If null is returned, it means we're redirecting to auth page
                // Exit early without changing status
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;

            case 'MICROSOFT TEAMS':
              tokenResponse = await getTeamsCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Early exit - Teams uses OAuth redirect
              if (!tokenResponse || tokenResponse.message === 'OAuth flow initiated') {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;

            case 'SLACK':
              tokenResponse = await getSlackCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              if (!tokenResponse) {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;

            case 'SHAREPOINT':
              tokenResponse = await getSharePointCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'LITMUS':
              tokenResponse = await getLitmusCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;

            case 'GOOGLEDRIVE':
              tokenResponse = await getGoogleDriveCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Early exit if we're redirecting to Google's consent page
              if (!tokenResponse) {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'GMAIL':
              tokenResponse = await getGmailCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Early exit if we're redirecting to Google's consent page
              if (!tokenResponse) {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'CALENDAR':
              tokenResponse = await getCalendarCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Early exit if we're redirecting to Google's consent page
              if (!tokenResponse) {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'EGNYTE STORAGE':
            case 'EGNYTE_STORAGE': // Enum/DB use underscore (ConnectorType.EGNYTE_STORAGE)
              tokenResponse = await getEgnyteCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Password grant OAuth - no redirect needed, token is returned directly
              // Function handles connector update internally, so we can continue
              break;
            case 'QUICKBOOKS':
              tokenResponse = await getQuickbookCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              // Early exit for QuickBooks if redirecting
              if (!tokenResponse) {
                // If null is returned, it means we're redirecting to auth page
                // Exit early without changing status
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'UNANET':
              tokenResponse = await getUnanetCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'ASROTRACK':
              tokenResponse = await getAsroTrackCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              if (!tokenResponse) {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'CONDUCTOR':
              tokenResponse = await getConductorCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              if (!tokenResponse) {
                setConnectingIds(prev => prev.filter(item => item !== id));
                return null;
              }
              break;
            case 'SYNCTRACK':
            case 'SYNC TRACK':
              tokenResponse = await getSyncTrackCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            case 'POSTGRESQL DATABASE':
            case 'POSTGRESQL': // mc_connector_type in DB is "POSTGRESQL"
              tokenResponse = await getPostgreSQLCredentialsAndCreateToken(id, currentItem.mcId, masterConnectors);
              break;
            default:
              throw new Error(`Unsupported connector: ${masterConnector.mcName}`);
          }
          // Get refresh URL from master connector
          const refreshUrl = masterConnector.mcRefreshUrl || masterConnector.mcTokenurl || null;
          console.log('Setting refreshUrl to:', refreshUrl);

          // Calculate the 14-day expiry date - consistently applied to all connector types
          const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
          const refreshTokenExpiry = new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString();

          // Special handling for different connector types (all use mcType for case-insensitive match)
          if (mcType === 'QUICKBOOKS' && tokenResponse) {
            const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;

            // Display real-time token information in console
            console.log('🔑 QUICKBOOKS REAL-TIME TOKEN RECEIVED:');
            console.log('✅ Access Token:', tokenResponse.access_token ? `${tokenResponse.access_token.substring(0, 50)}...` : 'Not provided');
            console.log('✅ Refresh Token:', tokenResponse.refresh_token ? `${tokenResponse.refresh_token.substring(0, 30)}...` : 'Not provided');
            console.log('⏰ Token Type:', tokenResponse.token_type || 'Bearer');
            console.log('⏰ Expires In:', `${tokenResponse.expires_in || 3600} seconds (${Math.round((tokenResponse.expires_in || 3600) / 60)} minutes)`);
            console.log('🔗 Refresh URL:', refreshUrl);
            console.log('📅 Token Generated At:', new Date().toISOString());

            updatedData = {
              ...updatedData,
              token: tokenResponse.access_token,
              refreshToken: tokenResponse.refresh_token,
              tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
              refreshUrl: refreshUrl,
              refreshTokenExpiry: refreshTokenExpiry // 14 days
            };
          } else if ((mcType === 'POSTGRESQL DATABASE' || mcType === 'POSTGRESQL') && tokenResponse) {
            // PostgreSQL doesn't use tokens, but we mark it as connected
            updatedData = {
              ...updatedData,
              token: 'connected', // Placeholder value to indicate connection
              status: 'Connected',
              connectedTime: new Date().toISOString()
            };
          } else if (
            (mcType === 'AUTODESK' || mcType === 'AUTODESK DOCS' || mcType === 'AUTODESK_DOCS') &&
            tokenResponse
          ) {
            // Handle the nested structure from Autodesk / Autodesk Docs (Forge OAuth)
            // Fix: Check if token objects exist before accessing their properties
            if (tokenResponse.primary_token && tokenResponse.secondary_token) {
              // Ensure expires_in values are valid numbers
              const primaryExpiresIn = parseInt(tokenResponse.primary_token.expires_in) || 3600;
              const secondaryExpiresIn = parseInt(tokenResponse.secondary_token.expires_in) || 3600;

              updatedData = {
                ...updatedData,
                token: tokenResponse.primary_token.access_token,
                refreshToken: tokenResponse.primary_token.refresh_token,
                tokenExpiry: new Date(Date.now() + (primaryExpiresIn * 1000)).toISOString(),
                secondaryToken: tokenResponse.secondary_token.access_token,
                secondaryTokenExpiry: new Date(Date.now() + (secondaryExpiresIn * 1000)).toISOString(),
                refreshUrl: refreshUrl,
                refreshTokenExpiry: refreshTokenExpiry // 14 days
              };
            } else {
              // Fallback if token structure is different than expected
              updatedData = {
                ...updatedData,
                token: tokenResponse.access_token || JSON.stringify(tokenResponse),
                refreshUrl: refreshUrl,
                tokenExpiry: new Date(Date.now() + 3600000).toISOString(), // default 1 hour
                refreshTokenExpiry: refreshTokenExpiry // 14 days
              };
            }
          } else if (mcType === 'GOOGLEDRIVE' && tokenResponse) {
            const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
            const ONE_EIGHTY_DAYS_MS = 180 * 24 * 60 * 60 * 1000;
            updatedData = {
              ...updatedData,
              token: tokenResponse.access_token,
              refreshToken: tokenResponse.refresh_token || updatedData.refreshToken || null,
              tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
              refreshUrl: masterConnector.mcRefreshUrl || 'https://oauth2.googleapis.com/token',
              refreshTokenExpiry: new Date(Date.now() + ONE_EIGHTY_DAYS_MS).toISOString()
            };

            // Handle Egnyte token response
          } else if ((mcType === 'EGNYTE' || mcType === 'EGNYTE STORAGE' || mcType === 'EGNYTE_STORAGE') && tokenResponse) {
            const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
            const ONE_EIGHTY_DAYS_MS = 180 * 24 * 60 * 60 * 1000;
            updatedData = {
              ...updatedData,
              token: tokenResponse.token || tokenResponse.access_token,
              refreshToken: tokenResponse.refreshToken || tokenResponse.refresh_token || updatedData.refreshToken || null,
              tokenExpiry: tokenResponse.tokenExpiry || new Date(Date.now() + expiresInMs).toISOString(),
              refreshUrl: masterConnector.mcRefreshUrl,
              refreshTokenExpiry: tokenResponse.refreshTokenExpiry || (tokenResponse.refresh_token ? new Date(Date.now() + ONE_EIGHTY_DAYS_MS).toISOString() : null)
            };

          } else if (mcType === 'UNANET' && tokenResponse) {
            // Handle UNANET token response - extract access_token from the response object
            console.log('🔑 UNANET REAL-TIME TOKEN RECEIVED:');
            console.log('✅ Access Token:', tokenResponse.access_token ? `${tokenResponse.access_token.substring(0, 20)}...` : 'Not provided');
            console.log('✅ Token Type:', tokenResponse.token_type || 'Bearer');
            console.log('✅ Warehouse ID:', tokenResponse.warehouse_id);
            console.log('✅ Role ID:', tokenResponse.role_id);
            console.log('🔗 Refresh URL:', refreshUrl);
            console.log('📅 Token Generated At:', new Date().toISOString());

            updatedData = {
              ...updatedData,
              token: tokenResponse.access_token, // Extract the access_token string
              refreshUrl: refreshUrl,
              tokenExpiry: new Date(Date.now() + 3600000).toISOString(), // default 1 hour
              refreshTokenExpiry: refreshTokenExpiry // 14 days
            };

            // Added this condition in the token processing section after the Autodesk handling
          } else if (
            (mcType === 'AUTODESK VAULT' || mcType === 'AUTODESK_VAULT' || mcType === 'AUTODESKVAULT' || mcType === 'VAULT') &&
            tokenResponse
          ) {
            const sessionExpiry = new Date(Date.now() + (8 * 60 * 60 * 1000)).toISOString(); // 8 hours

            updatedData = {
              ...updatedData,
              token: tokenResponse.accessToken || tokenResponse.id,
              sessionId: tokenResponse.id,
              tokenExpiry: sessionExpiry,
              ...(tokenResponse.url && { sessionUrl: tokenResponse.url })
            };
          } else if (mcType === 'UNANET' && tokenResponse) {
            // Handle UNANET token response (duplicate block - same as above, kept for structure)
            const tokenExpiry = new Date(Date.now() + (8 * 60 * 60 * 1000)).toISOString(); // 8 hours

            updatedData = {
              ...updatedData,
              token: tokenResponse.access_token,
              tokenType: tokenResponse.token_type,
              tokenExpiry: tokenExpiry,
              refreshTokenExpiry: refreshTokenExpiry, // 14 days
              warehouseId: tokenResponse.warehouse_id,
              roleId: tokenResponse.role_id,
              modules: tokenResponse.modules,
              features: tokenResponse.features
            };
          } else if (mcType === 'SHAREPOINT' && tokenResponse) {
            const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
            updatedData = {
              ...updatedData,
              token: typeof tokenResponse.access_token === 'string'
                ? tokenResponse.access_token
                : JSON.stringify(tokenResponse.access_token || tokenResponse),
              refreshToken: null,
              tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
              refreshUrl: null,
              refreshTokenExpiry: null,
              status: 'Connected',
              connectedTime: new Date().toISOString()
            };
          } else if (mcType === 'LITMUS' && tokenResponse) {
            const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
            updatedData = {
              ...updatedData,
              token: typeof tokenResponse.access_token === 'string'
                ? tokenResponse.access_token
                : JSON.stringify(tokenResponse.access_token || tokenResponse),
              refreshToken: null,
              tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
              refreshUrl: null,
              refreshTokenExpiry: null,
              status: 'Connected',
              connectedTime: new Date().toISOString()
            };
          } else if (mcType === 'SNOWFLAKE' && tokenResponse) {
            // Snowflake uses JDBC/key-pair; no OAuth tokens – mark as connected
            updatedData = {
              ...updatedData,
              token: 'connected',
              status: 'Connected',
              connectedTime: new Date().toISOString()
            };
          } else if (mcType === 'ASROTRACK' && tokenResponse) {
            const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
            const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
            updatedData = {
              ...updatedData,
              token: tokenResponse.access_token,
              refreshToken: tokenResponse.refresh_token || null,
              tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
              refreshUrl: masterConnector.mcRefreshUrl || masterConnector.mcTokenurl || null,
              refreshTokenExpiry: tokenResponse.refresh_token
                ? new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString()
                : null,
              status: 'Connected',
              connectedTime: new Date().toISOString()
            };
          } else if (mcType === 'CONDUCTOR' && tokenResponse) {
            const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
            const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;
            updatedData = {
              ...updatedData,
              token: tokenResponse.access_token,
              refreshToken: tokenResponse.refresh_token || null,
              tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
              refreshUrl: masterConnector.mcRefreshUrl || masterConnector.mcTokenurl || null,
              refreshTokenExpiry: tokenResponse.refresh_token
                ? new Date(Date.now() + FOURTEEN_DAYS_MS).toISOString()
                : null,
              status: 'Connected',
              connectedTime: new Date().toISOString()
            };
          } else if ((mcType === 'SYNCTRACK' || mcType === 'SYNC TRACK') && tokenResponse) {
            const expiresInMs = (tokenResponse.expires_in || 3600) * 1000;
            updatedData = {
              ...updatedData,
              token: tokenResponse.access_token,
              refreshToken: tokenResponse.refresh_token || null,
              tokenExpiry: new Date(Date.now() + expiresInMs).toISOString(),
              refreshUrl: masterConnector.mcRefreshUrl || masterConnector.mcTokenurl || null,
              refreshTokenExpiry: null,
              status: 'Connected',
              connectedTime: new Date().toISOString()
            };
          } else if (
            (mcType === 'SAGE INTACCT XML' || mcType === 'SAGE_INTACCT_XML') &&
            tokenResponse &&
            tokenResponse.sessionId
          ) {
            updatedData = {
              ...updatedData,
              token: tokenResponse.sessionId,
              refreshToken: tokenResponse.endpoint || null,
              tokenExpiry: tokenResponse.sessionTimeout
                ? new Date(tokenResponse.sessionTimeout).toISOString()
                : updatedData.tokenExpiry,
              status: 'Connected',
              connectedTime: new Date().toISOString()
            };
          } else if (tokenResponse) {
            updatedData = {
              ...updatedData,
              token: tokenResponse,
              refreshUrl: refreshUrl,
              refreshTokenExpiry: refreshTokenExpiry // 14 days
            };
          }
        } catch (error) {
          console.error('Connection generation error:', error);
          toast.error(`Failed to generate connection: ${error.message}`);
          setConnectingIds(prev => prev.filter(item => item !== id));
          return null;
        }
      }
      // Only after all token operations are done, set the status right before API call
      updatedData.status = newStatus;
      console.log('PUT request payload with status:', updatedData);
      // Make the PUT request with the updated data
      const response = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updatedData)
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      // Parse response data
      const responseData = await response.json();
      console.log('PUT response:', responseData);
      // Success message
      toast.success(`Connector ${newStatus.toLowerCase()} successfully`);
      // Now that the API call was successful, refresh the data
      await refreshData();
      return responseData;
    } catch (error) {
      console.error('Connection toggle error:', error);
      toast.error(`Error toggling connection: ${error.message}`);
      throw error;
    } finally {
      // Always remove the ID from connecting list when done
      setConnectingIds(prev => prev.filter(item => item !== id));
    }
  };

  // Handler to open parameters drawer
  const handleOpenParametersDrawer = (row) => {
    setSelectedConnector(row);
    setDrawerOpen(true);
  };

  // Handler to close parameters drawer
  const handleCloseParametersDrawer = () => {
    setDrawerOpen(false);
    // Refresh data when drawer closes to update any changes in UI
    refreshData();
  };

  // Handler to fetch and display temp_log rows for a given connector instance (ctd_id)
  const handleOpenTempLogDialog = async (connectorRow) => {
    const ctdId = connectorRow?.id;
    if (!ctdId) {
      return;
    }

    const mcName = connectorRow?.mcName || '';
    const connectorLabel = (typeof mcName === 'string' && mcName.trim()) ? mcName.trim() : 'Connector';

    setTempLogDialogOpen(true);
    setTempLogLoading(true);
    setTempLogRows([]);
    setTempLogError(null);
    setTempLogConnectorLabel(connectorLabel);

    try {
      // Select endpoint based on connector name
      let endpoint = 'synctrack/quickbooks/temp-log';
      const name = (mcName || '').toUpperCase();
      if (name.includes('GMAIL')) {
        endpoint = 'synctrack/gmail/temp-log';
      } else if (name.includes('CALENDAR')) {
        endpoint = 'synctrack/calendar/temp-log';
      } else if (name.includes('FUSION OPERATION')) {
        endpoint = 'synctrack/fusion/temp-log';
      } else if (name.includes('ASROTRACK')) {
        endpoint = 'synctrack/asrotrack/temp-log';
      } else if (name.includes('BUSINESS CENTRAL')) {
        endpoint = 'synctrack/business-central/temp-log';
      }

      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}?ctdId=${encodeURIComponent(ctdId)}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message || data?.error || `HTTP ${res.status}`);
      }

      setTempLogRows(data?.rows || []);
    } catch (e) {
      setTempLogError(e?.message || 'Failed to load temp_log rows');
    } finally {
      setTempLogLoading(false);
    }
  };

  // Handler to test QuickBooks customer fetching with fresh token
  const handleTestQuickBooksCustomers = async (connectorData) => {
    try {
      setTestLoading(true);
      setTestDialogOpen(true);
      setTestResults(null);

      // Get refresh token and mcId to get fresh token from QuickBooks
      const refreshToken = connectorData.refreshToken;
      const mcId = connectorData.mcId;

      if (!refreshToken) {
        throw new Error('No refresh token found. Cannot get fresh token from QuickBooks.');
      }

      if (!mcId) {
        throw new Error('Master connector ID not found.');
      }

      console.log('🔧 TESTING QUICKBOOKS WITH FRESH TOKEN');
      console.log('📝 Request Parameters:', {
        refreshToken: `${refreshToken.substring(0, 30)}...`,
        mcId: mcId.toString(),
        realmId: connectorData.realmId || null,
        endpoint: '/synctrack/quickbooks/test-with-fresh-token'
      });

      // Call the backend API to get fresh token and test customers
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}synctrack/quickbooks/test-with-fresh-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            refreshToken: refreshToken,
            mcId: mcId.toString(),
            realmId: connectorData.realmId || null
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to test with fresh token: ${response.status}`);
      }

      const data = await response.json();

      // Enhanced logging for real-time token display
      console.log('🔑 FRESH TOKEN RESPONSE FROM BACKEND:', data);

      // Extract token information from various possible response structures
      const tokenInfo = extractTokenInfo(data);
      console.log('🔍 EXTRACTED TOKEN INFO:', tokenInfo);

      // Enhanced data with extracted token info for better display
      const enhancedData = {
        ...data,
        // Normalize token fields for consistent display
        freshAccessToken: tokenInfo.access_token,
        newRefreshToken: tokenInfo.refresh_token,
        clientId: tokenInfo.client_id,
        clientSecret: tokenInfo.client_secret ? `${tokenInfo.client_secret.substring(0, 20)}...` : undefined,
        tokenType: tokenInfo.token_type || 'Bearer',
        expiresIn: tokenInfo.expires_in || 3600,
        tokenRefreshedAt: new Date().toISOString(),
        realTimeToken: true
      };

      setTestResults(enhancedData);

      // Display token details in console for immediate visibility
      if (tokenInfo.access_token) {
        console.log('✅ FRESH ACCESS TOKEN RECEIVED:', `${tokenInfo.access_token.substring(0, 50)}...`);
        console.log('✅ NEW REFRESH TOKEN:', tokenInfo.refresh_token ? `${tokenInfo.refresh_token.substring(0, 30)}...` : 'Not provided');
        console.log('⏰ TOKEN EXPIRES IN:', `${tokenInfo.expires_in || 3600} seconds (${Math.round((tokenInfo.expires_in || 3600) / 60)} minutes)`);
      }

      toast.success('✅ Fresh QuickBooks token retrieved and customer data fetched successfully!');
    } catch (error) {
      console.error('❌ Error testing QuickBooks customers:', error);
      setTestResults({ error: error.message });
      toast.error(`Test failed: ${error.message}`);
    } finally {
      setTestLoading(false);
    }
  };

  // Helper function to extract token information from various response structures
  const extractTokenInfo = (data) => {
    // Try different possible structures from backend response
    const sources = [
      data, // Direct properties
      data.tokenResponse, // Nested in tokenResponse
      data.tokenInfo, // Nested in tokenInfo
      data.freshToken, // Nested in freshToken
      data.token, // Nested in token
    ].filter(Boolean);

    const result = {};

    for (const source of sources) {
      if (source.access_token && !result.access_token) result.access_token = source.access_token;
      if (source.refresh_token && !result.refresh_token) result.refresh_token = source.refresh_token;
      if (source.token_type && !result.token_type) result.token_type = source.token_type;
      if (source.expires_in && !result.expires_in) result.expires_in = source.expires_in;
      if (source.client_id && !result.client_id) result.client_id = source.client_id;
      if (source.clientId && !result.client_id) result.client_id = source.clientId;
      if (source.client_secret && !result.client_secret) result.client_secret = source.client_secret;
      if (source.clientSecret && !result.client_secret) result.client_secret = source.clientSecret;
    }

    // Also check credentials object if it exists
    if (data.credentials) {
      if (data.credentials.clientId && !result.client_id) result.client_id = data.credentials.clientId;
      if (data.credentials.clientSecret && !result.client_secret) result.client_secret = data.credentials.clientSecret;
    }

    return result;
  };

  // Function to download token data as JSON file
  const downloadTokenData = (tokenData, filename = 'quickbooks_token.json') => {
    const dataStr = JSON.stringify(tokenData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = filename;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    console.log('📥 Token data downloaded:', filename);
  };

  const columns = [
    { field: 'mcName', headerName: 'Master Connector', width: 200 },
    { field: 'description', headerName: 'Connector Name', width: 250 },
    { field: 'status', headerName: 'Status', width: 150 },
    {
      field: 'action',
      headerName: 'Connection',
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isConnected = params.row.status === 'Connected';
        const isProcessing = connectingIds.includes(params.row.id);

        return (
          <Button
            variant="contained"
            color={isConnected ? "error" : "primary"}
            size="small"
            onClick={() => handleConnectionToggle(params.row.id, params.row.status)}
            disabled={isProcessing}
            sx={{
              minWidth: 110,
              width: 110,
              px: 1,
            }}
          >
            {isProcessing ? 'Processing...' : (isConnected ? 'Disconnect' : 'Connect')}
          </Button>
        );
      }
    },
    {
      field: 'parameters',
      headerName: 'Parameters',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const mcName = params?.row?.mcName;
        const tempLogLabel = (typeof mcName === 'string' && mcName.trim()) ? mcName.trim() : 'Connector';
        const supportsTempLog = !!params.row?.id;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={params.row.status === 'Connected' ? 'View Parameters (disconnect to edit)' : 'Edit Parameters'}>
              <IconButton
                size="small"
                onClick={() => handleOpenParametersDrawer(params.row)}
                color="primary"
              >
                <Settings />
              </IconButton>
            </Tooltip>

            <Tooltip title={`View ${tempLogLabel} temp_log (by ctd_id)`}>
              <IconButton
                size="small"
                onClick={() => handleOpenTempLogDialog(params.row)}
                color="info"
                disabled={!supportsTempLog || !params.row?.id}
              >
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        );
      }
    },
    // COMMENTED OUT: QuickBooks test button - high risk, disabled for production
    // {
    //   field: 'test',
    //   headerName: 'Test',
    //   width: 120,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params) => {
    //     // Show test button only for connected QuickBooks connectors
    //     const isQuickBooks = params.row.mcName === 'Quickbook';
    //     const isConnected = params.row.status === 'Connected';

    //     if (!isQuickBooks || !isConnected) {
    //       return null;
    //     }

    //     return (
    //       <Tooltip title="Test QuickBooks Connection">
    //         <IconButton
    //           size="small"
    //           onClick={() => handleTestQuickBooksCustomers(params.row)}
    //           color="secondary"
    //           disabled={testLoading}
    //         >
    //           <PlayArrow />
    //         </IconButton>
    //       </Tooltip>
    //     );
    //   }
    // }
  ];

  // Create options array for the dropdown
  const masterConnectorOptions = masterConnectors.map(mc => ({
    value: mc.mcRecId || mc.mcId,
    label: mc.mcName || `ID: ${mc.mcRecId || mc.mcId}`
  }));

  // Convert to simple string array if DynamicContainer doesn't support object options
  const formFields = [
    {
      name: 'mcId',
      label: 'Master Connector',
      required: true,
      type: 'select',
      options: masterConnectorOptions.map(option => option.label),
      transform: (selectedLabel) => {
        const option = masterConnectorOptions.find(opt => opt.label === selectedLabel);
        return option ? option.value : selectedLabel;
      },
      validate: (value) => !value ? 'Master Connector is required' : null,
      disableOnEdit: true,
      helperText: 'Master connector cannot be changed after creation'
    },
    {
      name: 'description',
      label: 'Connector Name',
      required: true,
      placeholder: 'e.g., QuickBooks - Company A',
      validate: (value) => !value || !String(value).trim() ? 'Connector Name is required' : null,
    }
  ];

  const preprocessFormData = (formData) => {
    // If the selected value is a display name, convert it to the ID
    let mcId = formData.mcId;
    const option = masterConnectorOptions.find(opt => opt.label === mcId);
    if (option) {
      mcId = option.value;
    }

    return {
      ...formData,
      mcId: mcId, // Use the ID value
      // Add default values for required fields that aren't in the form
      token: formData.token || 'default-token',
      // tenantId: formData.tenantId || tenantId || null,
      deployment: formData.deployment || 'Development',
      // Always set status to Disconnected for new entries
      status: 'Disconnected'
    };
  };

  const initialFormData = {
    mcId: '',
    description: ''
  };

  // Prepare edit data: Convert mcId (UUID) to master connector name (label) for display
  const prepareEditData = (row) => {
    // Find the master connector name from mcId
    let mcNameLabel = '';
    if (row.mcId) {
      const masterConnector = masterConnectors.find(mc =>
      (mc.mcRecId?.toString() === row.mcId?.toString() ||
        mc.mcId?.toString() === row.mcId?.toString())
      );
      if (masterConnector) {
        mcNameLabel = masterConnector.mcName;
      } else if (row.mcName) {
        // Fallback: use mcName if it's already in the row data
        mcNameLabel = row.mcName;
      }
    }

    return {
      ...row,
      mcId: mcNameLabel, // Convert UUID to label for display in dropdown
      description: row.description || ''
    };
  };

  // const breadcrumbPaths = generateBreadcrumbPath('connectortoken'); // Commented out - breadcrumbs not needed

  return (
    <>
      <DynamicContainer
        title="Connector Token"
        icon={Key}
        columns={columns}
        formFields={formFields}
        initialFormData={initialFormData}
        idField="id"
        // breadcrumbPaths={breadcrumbPaths} // Commented out - breadcrumbs not needed
        rows={connectorTokens}
        onCreate={(data) => handleCreate(preprocessFormData(data))}
        onUpdate={(id, data) => handleUpdate(id, preprocessFormData(data))}
        onDelete={handleDelete}
        getDeleteBlockReason={getDeleteBlockReason}
        prepareEditData={prepareEditData}
        preprocessFormData={preprocessFormData}
        isCdxUser={isCdxUser()}
        functionCode="Connector"
        isLoading={isLoading}
      />

      {/* Token Parameter Drawer */}
      <TokenParameterDrawer
        open={drawerOpen}
        onClose={handleCloseParametersDrawer}
        connectorData={selectedConnector}
        masterConnectors={masterConnectors}
      />

      {/* Test Results Dialog */}
      <Dialog
        open={testDialogOpen}
        onClose={() => setTestDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>QuickBooks Customer Test Results (Using Fresh Token)</DialogTitle>
        <DialogContent>
          {testLoading ? (
            <Typography>Getting fresh token from QuickBooks and fetching customer data...</Typography>
          ) : testResults ? (
            <Box>
              {testResults.error ? (
                <Typography color="error">Error: {testResults.error}</Typography>
              ) : (
                <Box>
                  {/* Parameters Used for Customer Fetching */}
                  <Box mb={2}>
                    <Typography variant="h6" gutterBottom color="primary">
                      🔧 Request Parameters Sent to Backend
                    </Typography>
                    <Typography variant="body2">
                      <strong>Refresh Token (from DB):</strong> {testResults.refreshToken ?
                        `${testResults.refreshToken.substring(0, 30)}...` :
                        'Not available'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Master Connector ID:</strong> {testResults.mcId || 'Not available'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Realm ID:</strong> {testResults.realmId || 'Not provided'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Backend Endpoint:</strong> /synctrack/quickbooks/test-with-fresh-token
                    </Typography>
                    <Typography variant="body2">
                      <strong>HTTP Method:</strong> POST
                    </Typography>
                  </Box>

                  {/* QuickBooks Credentials Used */}
                  <Box mb={2}>
                    <Typography variant="h6" gutterBottom color="info.main">
                      🔐 QuickBooks Credentials Used
                    </Typography>
                    <Typography variant="body2">
                      <strong>Client ID:</strong> {
                        testResults.clientId ? `${testResults.clientId}` :
                          testResults.credentials?.clientId ? `${testResults.credentials.clientId}` :
                            'Should be returned by backend'
                      }
                    </Typography>
                    <Typography variant="body2">
                      <strong>Client Secret:</strong> {
                        testResults.clientSecret ? `${testResults.clientSecret.substring(0, 20)}...` :
                          testResults.credentials?.clientSecret ? `${testResults.credentials.clientSecret.substring(0, 20)}...` :
                            'Should be returned by backend (masked for security)'
                      }
                    </Typography>
                    <Typography variant="body2">
                      <strong>OAuth Endpoint:</strong> https://oauth.platform.intuit.com/oauth2/v1/tokens/bearer
                    </Typography>
                  </Box>

                  {/* Fresh Token from QuickBooks */}
                  <Box mb={2}>
                    <Typography variant="h6" gutterBottom color="secondary">
                      🔑 Fresh Token Response from QuickBooks
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold', mb: 1 }}>
                      ✅ This is the FRESH token directly from QuickBooks API (NOT from database)
                    </Typography>

                    <Typography variant="body2">
                      <strong>Fresh Access Token:</strong> {
                        testResults.freshAccessToken ? `${testResults.freshAccessToken.substring(0, 50)}...` :
                          testResults.access_token ? `${testResults.access_token.substring(0, 50)}...` :
                            testResults.tokenResponse?.access_token ? `${testResults.tokenResponse.access_token.substring(0, 50)}...` :
                              testResults.tokenInfo?.access_token ? `${testResults.tokenInfo.access_token.substring(0, 50)}...` :
                                'Backend should return fresh access token'
                      }
                    </Typography>

                    <Typography variant="body2">
                      <strong>New Refresh Token:</strong> {
                        testResults.newRefreshToken ? `${testResults.newRefreshToken.substring(0, 30)}...` :
                          testResults.refresh_token ? `${testResults.refresh_token.substring(0, 30)}...` :
                            testResults.tokenResponse?.refresh_token ? `${testResults.tokenResponse.refresh_token.substring(0, 30)}...` :
                              'Backend should return new refresh token'
                      }
                    </Typography>

                    <Typography variant="body2">
                      <strong>Token Type:</strong> {
                        testResults.token_type ||
                        testResults.tokenResponse?.token_type ||
                        testResults.tokenInfo?.token_type ||
                        'Bearer'
                      }
                    </Typography>

                    <Typography variant="body2">
                      <strong>Expires In:</strong> {
                        testResults.expires_in ||
                        testResults.tokenResponse?.expires_in ||
                        testResults.tokenInfo?.expires_in ||
                        '3600'
                      } seconds ({Math.round(((testResults.expires_in || testResults.tokenResponse?.expires_in || testResults.tokenInfo?.expires_in || 3600) / 60))} minutes)
                    </Typography>

                    {testResults.tokenRefreshedAt && (
                      <Typography variant="body2">
                        <strong>Refreshed At:</strong> {testResults.tokenRefreshedAt}
                      </Typography>
                    )}

                    <Typography variant="body2" sx={{ color: 'success.main', mt: 1 }}>
                      <strong>Used For Customer API:</strong> ✅ This fresh token was used to query customers
                    </Typography>
                  </Box>

                  {/* Token Information (Legacy Support) */}
                  {testResults.tokenInfo && !testResults.freshToken && !testResults.access_token && (
                    <Box mb={2}>
                      <Typography variant="h6" gutterBottom color="primary">
                        🔑 Token Information
                      </Typography>
                      <Typography variant="body2">
                        <strong>Source:</strong> Fresh token from QuickBooks (not from database)
                      </Typography>
                      <Typography variant="body2">
                        <strong>Token Refreshed:</strong> ✅ Yes
                      </Typography>
                      {testResults.tokenInfo.newTokenExpiry && (
                        <Typography variant="body2">
                          <strong>Token Expires In:</strong> {testResults.tokenInfo.newTokenExpiry} seconds
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Customer Data */}
                  <Typography variant="h6" gutterBottom color="primary">
                    👥 Customer Data Response
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Customer Count:</strong> {testResults.QueryResponse?.Customer?.length || 0}
                  </Typography>
                  <pre style={{
                    background: '#f5f5f5',
                    padding: '16px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '400px'
                  }}>
                    {JSON.stringify(testResults, null, 2)}
                  </pre>
                </Box>
              )}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          {testResults && !testResults.error && (
            <Button
              onClick={() => downloadTokenData(testResults, `quickbooks_test_token_${new Date().toISOString().split('T')[0]}.json`)}
              variant="outlined"
              color="secondary"
            >
              📥 Download Test Data
            </Button>
          )}
          <Button onClick={() => setTestDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Fresh Token Display Dialog */}
      <Dialog
        open={tokenDialogOpen}
        onClose={() => setTokenDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>🔑 Fresh QuickBooks Token Received!</DialogTitle>
        <DialogContent>
          {freshTokenDetails ? (
            <Box>
              <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 'bold', mb: 2 }}>
                ✅ Connection successful! Here are the fresh token details from QuickBooks:
              </Typography>

              {/* QuickBooks Credentials */}
              <Box mb={2}>
                <Typography variant="h6" gutterBottom color="primary">
                  🔐 QuickBooks App Credentials
                </Typography>
                <Typography variant="body2">
                  <strong>Client ID:</strong> {freshTokenDetails.client_id}
                </Typography>
                <Typography variant="body2">
                  <strong>Client Secret:</strong> {freshTokenDetails.client_secret}
                </Typography>
                <Typography variant="body2">
                  <strong>OAuth Endpoint:</strong> {freshTokenDetails.oauth_endpoint}
                </Typography>
              </Box>

              {/* Fresh Token Details */}
              <Box mb={2}>
                <Typography variant="h6" gutterBottom color="secondary">
                  🔑 Fresh Access Token (Just Received)
                </Typography>
                <Typography variant="body2">
                  <strong>Access Token:</strong> {freshTokenDetails.access_token.substring(0, 50)}...
                </Typography>
                <Typography variant="body2">
                  <strong>Refresh Token:</strong> {freshTokenDetails.refresh_token.substring(0, 30)}...
                </Typography>
                <Typography variant="body2">
                  <strong>Token Type:</strong> {freshTokenDetails.token_type}
                </Typography>
                <Typography variant="body2">
                  <strong>Expires In:</strong> {freshTokenDetails.expires_in} seconds ({freshTokenDetails.expires_in_minutes} minutes)
                </Typography>
                <Typography variant="body2">
                  <strong>Received At:</strong> {new Date(freshTokenDetails.received_at).toLocaleString()}
                </Typography>
              </Box>

              {/* Status */}
              <Box>
                <Typography variant="h6" gutterBottom color="info.main">
                  📊 Status
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  ✅ Token successfully saved to database
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  ✅ Connector status updated to 'Connected'
                </Typography>
                <Typography variant="body2" sx={{ color: 'info.main' }}>
                  🔄 You can now use this connector for scheduled operations
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography>Loading token details...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => downloadTokenData(freshTokenDetails, `quickbooks_fresh_token_${new Date().toISOString().split('T')[0]}.json`)}
            variant="outlined"
            color="secondary"
          >
            📥 Download Token Data
          </Button>
          <Button onClick={() => setTokenDialogOpen(false)} variant="contained" color="primary">
            Got it!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Temp Log Dialog (debug helper for connector connect flow) */}
      <Dialog
        open={tempLogDialogOpen}
        onClose={() => setTempLogDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>🧾 {tempLogConnectorLabel} temp_log details</DialogTitle>
        <DialogContent>
          {tempLogLoading ? (
            <Typography>Loading temp_log rows...</Typography>
          ) : tempLogError ? (
            <Typography color="error">{tempLogError}</Typography>
          ) : tempLogRows?.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {tempLogRows.map((row) => (
                <Box
                  key={row.id}
                  sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {row.eventType} - {row.status}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.createdAt}
                  </Typography>

                  {row.requestPayload ? (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary">Request</Typography>
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                        {row.requestPayload}
                      </pre>
                    </Box>
                  ) : null}

                  {row.responsePayload ? (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary">Response</Typography>
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                        {row.responsePayload}
                      </pre>
                    </Box>
                  ) : null}

                  {row.errorMessage ? (
                    <Box mt={1}>
                      <Typography variant="caption" color="error.main">Error</Typography>
                      <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                        {row.errorMessage}
                      </pre>
                    </Box>
                  ) : null}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography>No temp_log rows found for this connector.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTempLogDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ConnectorToken;